"use client";

import { Check, Link2, MousePointer2, Search, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AisSubpageCopy } from "./ais-page-copy";

type Story = AisSubpageCopy["story"];

/**
 * Scroll story (giga.ai, light): a pinned blueprint stage that rebuilds itself
 * as each chapter scrolls past, from a list of links to a question, the
 * sources being read, the cited answer, zero click and finally your business
 * as the source. Each element is shown with `data-on`; CSS does the motion.
 */
export function CcAisStory({ copy }: { copy: Story }) {
  const [step, setStep] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const items = refs.current.filter(Boolean) as HTMLLIElement[];
    if (!items.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            setStep(Number((entry.target as HTMLElement).dataset.index));
        }
      },
      // Phones keep the stage pinned on top, so the active line sits lower.
      {
        rootMargin:
          window.innerWidth < 810 ? "-72% 0px -18% 0px" : "-45% 0px -45% 0px",
      },
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  const on = (from: number, to = 99) =>
    step >= from && step <= to ? "true" : undefined;
  const active = copy.steps[step];
  const [before, after] = copy.answer.split(copy.brand);

  return (
    <section
      className="cc-section cc-ais-story"
      id={copy.id}
      aria-labelledby="cc-ais-story-title"
      data-cc-nav="light"
      {...devSectionAttr("ai-sok-story")}
    >
      <div className="cc-ais-story__container">
        <header className="cc-ais-head">
          <p className="cc-ais-mono">{copy.eyebrow}</p>
          <h2 id="cc-ais-story-title" className="cc-ais-title">
            {copy.title}
          </h2>
          <p className="cc-ais-intro">{copy.intro}</p>
        </header>

        <div className="cc-ais-story__grid">
          <ol className="cc-ais-story__steps">
            {copy.steps.map((item, index) => (
              <li
                key={item.id}
                ref={(node) => {
                  refs.current[index] = node;
                }}
                data-index={index}
                className="cc-ais-story__step"
                data-active={index === step ? "true" : undefined}
              >
                <span className="cc-ais-mono">{item.stamp}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ol>

          <div className="cc-ais-story__sticky">
            <figure
              className="cc-ais-stage"
              data-step={step}
              aria-hidden="true"
            >
              <span className="cc-ais-stage__stamp cc-ais-mono">
                {active.stamp}
              </span>
              <span className="cc-ais-stage__count cc-ais-mono">
                {String(step + 1).padStart(2, "0")} /{" "}
                {String(copy.steps.length).padStart(2, "0")}
              </span>

              {/* 01: a classic results page */}
              <div className="cc-ais-stage__serp" data-on={on(0, 0)}>
                <div className="cc-ais-stage__search">
                  <Search size={15} strokeWidth={2.2} />
                  {copy.query}
                </div>
                <small>{copy.labels.results}</small>
                <ul>
                  {Array.from({ length: 6 }, (_, i) => (
                    <li key={i} style={{ ["--i" as string]: i }}>
                      {i === 0 ? <em>{copy.labels.ad}</em> : null}
                      <b />
                      <i />
                      <i />
                    </li>
                  ))}
                </ul>
              </div>

              {/* 02: the whole question */}
              <div className="cc-ais-stage__prompt" data-on={on(1)}>
                <span>{copy.question}</span>
              </div>

              {/* 03: sources being read */}
              <svg
                className="cc-ais-stage__wires"
                viewBox="0 0 400 300"
                preserveAspectRatio="none"
                data-on={on(2, 2)}
              >
                {[
                  "M200 70 C120 80 90 110 70 140",
                  "M200 70 C150 90 150 120 150 150",
                  "M200 70 C250 90 250 120 250 150",
                  "M200 70 C280 80 310 110 330 140",
                ].map((d, i) => (
                  <path
                    key={i}
                    d={d}
                    pathLength={1}
                    style={{ ["--i" as string]: i }}
                  />
                ))}
              </svg>
              <ul
                className="cc-ais-stage__sources"
                data-on={on(2)}
                data-row={on(3)}
              >
                {copy.sources.map((source, i) => (
                  <li
                    key={source.id}
                    style={{ ["--i" as string]: i }}
                    data-you={i === 0 ? on(5) : undefined}
                    data-dim={i > 0 ? on(4) : undefined}
                  >
                    <b>{i + 1}</b>
                    <span>
                      <strong>{source.title}</strong>
                      <small>{source.text}</small>
                    </span>
                    {i === 0 ? (
                      <em className="cc-ais-stage__you">{copy.labels.you}</em>
                    ) : null}
                  </li>
                ))}
              </ul>
              <span className="cc-ais-stage__reading" data-on={on(2, 2)}>
                <Sparkles size={13} strokeWidth={2.2} />
                {copy.labels.searching}
              </span>

              {/* 04: the cited answer */}
              <div
                className="cc-ais-stage__answer"
                data-on={on(3)}
                data-you={on(5)}
              >
                <span className="cc-ais-stage__avatar">
                  <Sparkles size={14} strokeWidth={2.2} />
                </span>
                <p>
                  {before}
                  <mark>{copy.brand}</mark>
                  {after}
                  <sup>1</sup>
                  <sup>2</sup>
                  <sup>4</sup>
                </p>
              </div>

              {/* 05: zero click */}
              <div className="cc-ais-stage__zero" data-on={on(4, 4)}>
                <span className="cc-ais-stage__done">
                  <Check size={14} strokeWidth={2.6} />
                  {copy.labels.noClick}
                </span>
                <span className="cc-ais-stage__click">
                  <MousePointer2 size={14} strokeWidth={2.2} />
                  {copy.labels.click}
                  <Link2 size={13} strokeWidth={2.2} />1
                </span>
              </div>
            </figure>
            <p className="cc-ais-story__note">{copy.exampleNote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
